import React, { useState, useEffect, useRef } from 'react';
import { emptyEvent, geocode, detectCountryCode, validateEvent, uploadImage } from './eventsStorage';

// Convert MM/DD/YYYY ↔ YYYY-MM-DD for the <input type="date">
function slashToIso(str) {
  const s = String(str || '').trim();
  if (!s) return '';
  const parts = s.split('/');
  if (parts.length !== 3) return '';
  const [m, d, y] = parts;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function isoToSlash(str) {
  const s = String(str || '').trim();
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length !== 3) return '';
  const [y, m, d] = parts;
  return `${String(parseInt(m, 10))}/${String(parseInt(d, 10))}/${y}`;
}

export default function EventForm({ initial, onSave, onCancel, onDelete }) {
  const seed = initial || emptyEvent();
  const [p, setP] = useState(seed.properties);
  const [coords, setCoords] = useState(seed.geometry?.coordinates || [0, 0]);
  const [isoDate, setIsoDate] = useState(slashToIso(seed.properties.date));
  const [errors, setErrors] = useState([]);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoMsg, setGeoMsg] = useState(null);

  // Auto-geocode bookkeeping — refs so updates don't re-render
  const initialQueryRef  = useRef('');
  const lastGeoQueryRef  = useRef('');
  const coordsTouchedRef = useRef(false);

  // Image upload
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [uploadPct, setUploadPct]     = useState(0);
  const [uploadMsg, setUploadMsg]     = useState(null);

  useEffect(() => {
    setP(seed.properties);
    setCoords(seed.geometry?.coordinates || [0, 0]);
    setIsoDate(slashToIso(seed.properties.date));
    setErrors([]);
    setGeoMsg(null);
    initialQueryRef.current = [seed.properties.address, seed.properties.city, seed.properties.country]
      .filter(Boolean).join(', ').toLowerCase().trim();
    lastGeoQueryRef.current  = initialQueryRef.current;
    coordsTouchedRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const setField = (name, value) => setP(prev => ({ ...prev, [name]: value }));

  const handleDate = (iso) => {
    setIsoDate(iso);
    setField('date', isoToSlash(iso));
  };

  // Manual edit of lat/lng → suspend auto-geocoding for this session
  const handleLng = (v) => {
    coordsTouchedRef.current = true;
    setCoords([parseFloat(v) || 0, coords[1]]);
  };
  const handleLat = (v) => {
    coordsTouchedRef.current = true;
    setCoords([coords[0], parseFloat(v) || 0]);
  };

  /** Manual button — always re-geocodes, ignoring the touch guard. */
  const doGeocode = async () => {
    const q = [p.address, p.city, p.country].filter(Boolean).join(', ').trim();
    if (!q) { setGeoMsg({ type: 'error', text: 'Escribe dirección y ciudad primero' }); return; }
    setGeoBusy(true);
    setGeoMsg(null);
    try {
      const res = await geocode(q, { countryCode: detectCountryCode(p.country) });
      setCoords([res.lng, res.lat]);
      coordsTouchedRef.current = false; // explicit re-geocode resets the lock
      lastGeoQueryRef.current = q.toLowerCase();
      setGeoMsg({ type: 'ok', text: `Ubicación: ${res.display}` });
    } catch (err) {
      setGeoMsg({ type: 'error', text: err.message || 'No se encontró la dirección' });
    } finally {
      setGeoBusy(false);
    }
  };

  /** Auto-geocode after the user stops typing for 800ms.
   *  Only fires when:
   *    - both address AND city have content (otherwise it's too vague)
   *    - the combined query actually changed (vs. initial / last fired)
   *    - the user hasn't manually edited lat/lng since opening the form */
  useEffect(() => {
    if (coordsTouchedRef.current) return;
    if (!p.address || !p.city) return;

    const query = [p.address, p.city, p.country].filter(Boolean).join(', ').trim();
    const norm  = query.toLowerCase();
    if (!norm || query.length < 6) return;
    if (norm === initialQueryRef.current) return;   // unchanged from initial
    if (norm === lastGeoQueryRef.current) return;   // already tried this exact query

    const timer = setTimeout(async () => {
      lastGeoQueryRef.current = norm;
      setGeoBusy(true);
      setGeoMsg(null);
      try {
        const res = await geocode(query, { countryCode: detectCountryCode(p.country), country: p.country });
        // User might have started typing in lat/lng during the 800ms wait
        if (coordsTouchedRef.current) return;
        setCoords([res.lng, res.lat]);
        const place = String(res.display || '').slice(0, 90);
        setGeoMsg({ type: 'ok', text: `Auto: ${place}` });
      } catch (err) {
        setGeoMsg({ type: 'error', text: err.message || 'No se encontró' });
      } finally {
        setGeoBusy(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.address, p.city, p.country]);

  /* ── Image upload (drag-drop + click to browse) ── */
  const startUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setUploadMsg({ type: 'error', text: 'Solo imágenes (JPG, PNG, WebP, GIF)' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadMsg({ type: 'error', text: 'Máximo 5 MB' });
      return;
    }
    setUploadMsg(null);
    setUploading(true);
    setUploadPct(0);
    try {
      const res = await uploadImage(file, (pct) => setUploadPct(pct));
      setField('image_url', res.url);
      setUploadMsg({ type: 'ok', text: `Subida correctamente (${(res.size/1024).toFixed(0)} KB)` });
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.message || 'Error al subir' });
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading) return;

    // 1. A real file dropped from the desktop?
    const file = e.dataTransfer?.files?.[0];
    if (file) { startUpload(file); return; }

    // 2. A URL dragged from another browser tab?
    const txt = e.dataTransfer?.getData('text/uri-list')
             || e.dataTransfer?.getData('text/plain');
    if (txt && /^https?:\/\//i.test(txt.trim())) {
      setField('image_url', txt.trim());
      setUploadMsg({ type: 'ok', text: 'URL recibida' });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onPickFile = () => {
    if (uploading || !fileInputRef.current) return;
    // Defer to next tick so the current click event finishes propagating
    // before the file dialog opens (prevents any odd re-entry on slower hosts).
    setTimeout(() => fileInputRef.current?.click(), 0);
  };
  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (file) startUpload(file);
    if (e.target) e.target.value = '';
  };

  const removeImage = (e) => {
    e?.stopPropagation();
    setField('image_url', '');
    setUploadMsg(null);
  };

  const submit = (e) => {
    e.preventDefault();
    const feature = {
      type: 'Feature',
      properties: { ...p },
      geometry: { type: 'Point', coordinates: [coords[0], coords[1]] },
    };
    const errs = validateEvent(feature);
    if (errs.length) {
      setErrors(errs);
      return;
    }
    onSave(feature);
  };

  return (
    <form className="ck-admin-form" onSubmit={submit} noValidate>
      <div className="ck-admin-form__grid">
        <label className="ck-admin-field ck-admin-field--full">
          <span className="ck-admin-field__label">Venue / Título *</span>
          <input
            type="text"
            value={p.title}
            onChange={e => setField('title', e.target.value)}
            className="ck-admin-field__input"
            required
            placeholder="Auditorio Telmex"
          />
        </label>

        <label className="ck-admin-field">
          <span className="ck-admin-field__label">Fecha *</span>
          <input
            type="date"
            value={isoDate}
            onChange={e => handleDate(e.target.value)}
            className="ck-admin-field__input"
            required
          />
        </label>

        <label className="ck-admin-field">
          <span className="ck-admin-field__label">Ciudad *</span>
          <input
            type="text"
            value={p.city}
            onChange={e => setField('city', e.target.value)}
            className="ck-admin-field__input"
            required
            placeholder="Guadalajara"
          />
        </label>

        <label className="ck-admin-field ck-admin-field--full">
          <span className="ck-admin-field__label">Dirección</span>
          <input
            type="text"
            value={p.address}
            onChange={e => setField('address', e.target.value)}
            className="ck-admin-field__input"
            placeholder="Av. Mariano Otero 1499, Verde Valle"
          />
        </label>

        <label className="ck-admin-field">
          <span className="ck-admin-field__label">País</span>
          <input
            type="text"
            value={p.country}
            onChange={e => setField('country', e.target.value)}
            className="ck-admin-field__input"
            placeholder="México / Spain / USA"
          />
        </label>

        <div className="ck-admin-field ck-admin-field--full">
          <span className="ck-admin-field__label">Imagen / Flyer</span>

          {/* File input lives OUTSIDE the dropzone div so its programmatic
              click events can't bubble back to the dropzone's onClick and
              cause a recursive loop. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFileChosen}
            style={{ display: 'none' }}
            disabled={uploading}
          />

          <div
            className={`ck-admin-drop ${dragActive ? 'is-active' : ''} ${uploading ? 'is-uploading' : ''} ${p.image_url ? 'has-image' : ''}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={(e) => {
              // Don't trigger the picker when the user clicks the inner buttons
              if (e.target.closest('button')) return;
              onPickFile();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPickFile(); } }}
            aria-label="Arrastrar o seleccionar imagen"
          >
            {p.image_url && !uploading ? (
              <div className="ck-admin-drop__preview">
                <img
                  src={p.image_url}
                  alt="Flyer"
                  onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                />
                <div className="ck-admin-drop__preview-meta">
                  <span className="ck-admin-drop__preview-label">Imagen actual</span>
                  <span className="ck-admin-drop__preview-url" title={p.image_url}>{p.image_url}</span>
                  <div className="ck-admin-drop__preview-actions">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onPickFile(); }} className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm">
                      <i className="fa-solid fa-arrow-rotate-right" /> Reemplazar
                    </button>
                    <button type="button" onClick={removeImage} className="ck-admin-btn ck-admin-btn--danger ck-admin-btn--sm">
                      <i className="fa-solid fa-xmark" /> Quitar
                    </button>
                  </div>
                </div>
              </div>
            ) : uploading ? (
              <div className="ck-admin-drop__progress">
                <i className="fa-solid fa-spinner fa-spin" />
                <p>Subiendo… {uploadPct}%</p>
                <div className="ck-admin-drop__bar"><span style={{ width: `${uploadPct}%` }} /></div>
              </div>
            ) : (
              <div className="ck-admin-drop__hint">
                <i className="fa-solid fa-cloud-arrow-up" />
                <p><strong>Arrastra una imagen aquí</strong></p>
                <p className="ck-admin-drop__small">o haz clic para elegir · JPG, PNG, WebP, GIF · máx 5 MB</p>
              </div>
            )}
          </div>

          {/* Manual URL fallback for cases without dragging */}
          <input
            type="url"
            value={p.image_url}
            onChange={e => setField('image_url', e.target.value)}
            className="ck-admin-field__input ck-admin-drop__url"
            placeholder="O pega una URL: https://…"
          />

          {uploadMsg && (
            <p className={`ck-admin-form__hint ck-admin-form__hint--${uploadMsg.type}`}>
              {uploadMsg.text}
            </p>
          )}
        </div>

        <label className="ck-admin-field">
          <span className="ck-admin-field__label">Link de boletos</span>
          <input
            type="url"
            value={p.ticket_url}
            onChange={e => setField('ticket_url', e.target.value)}
            className="ck-admin-field__input"
            placeholder="https://www.ticketmaster.com.mx"
          />
        </label>

        <label className="ck-admin-field">
          <span className="ck-admin-field__label">Link VIP</span>
          <input
            type="url"
            value={p.vipticket_url}
            onChange={e => setField('vipticket_url', e.target.value)}
            className="ck-admin-field__input"
            placeholder="(opcional)"
          />
        </label>

        <div className="ck-admin-field ck-admin-field--full">
          <div className="ck-admin-field__row">
            <label className="ck-admin-field ck-admin-field--inline">
              <span className="ck-admin-field__label">Latitud *</span>
              <input
                type="number"
                step="any"
                value={coords[1]}
                onChange={e => handleLat(e.target.value)}
                className="ck-admin-field__input"
                required
              />
            </label>
            <label className="ck-admin-field ck-admin-field--inline">
              <span className="ck-admin-field__label">Longitud *</span>
              <input
                type="number"
                step="any"
                value={coords[0]}
                onChange={e => handleLng(e.target.value)}
                className="ck-admin-field__input"
                required
              />
            </label>
            <button
              type="button"
              onClick={doGeocode}
              disabled={geoBusy}
              className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm"
              title="Buscar coordenadas desde la dirección"
            >
              {geoBusy ? (
                <><i className="fa-solid fa-spinner fa-spin" /> Buscando…</>
              ) : (
                <><i className="fa-solid fa-location-crosshairs" /> Buscar coords</>
              )}
            </button>
          </div>
          {geoMsg && (
            <p className={`ck-admin-form__hint ck-admin-form__hint--${geoMsg.type}`}>
              {geoMsg.text}
            </p>
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <ul className="ck-admin-form__errors">
          {errors.map((e, i) => <li key={i}><i className="fa-solid fa-circle-exclamation" /> {e}</li>)}
        </ul>
      )}

      <div className="ck-admin-form__actions">
        <div>
          {onDelete && (
            <button type="button" onClick={onDelete} className="ck-admin-btn ck-admin-btn--danger">
              <i className="fa-solid fa-trash" /> Eliminar
            </button>
          )}
        </div>
        <div className="ck-admin-form__actions-right">
          <button type="button" onClick={onCancel} className="ck-admin-btn ck-admin-btn--ghost">
            Cancelar
          </button>
          <button type="submit" className="ck-admin-btn ck-admin-btn--primary">
            <i className="fa-solid fa-check" /> Guardar
          </button>
        </div>
      </div>
    </form>
  );
}
